import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, Location } from 'react-router-dom';
import { MenuItem as MenuItemType, MenuProps } from '../types';
import { FaLinkedin } from 'react-icons/fa';
import { BiSolidFileDoc } from 'react-icons/bi';
import { useI18n } from '../i18n';
import { en, km } from './Menu.copy';

interface MenuItemProps {
  item: MenuItemType;
  location: Location;
  handleLinkClick: (e: React.MouseEvent<HTMLAnchorElement>, path: string) => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, location, handleLinkClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const isActive = item.to && location.pathname === item.to;
  
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  };
  
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 300); // 300ms delay before closing
  };
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // For items with children (dropdown menus)
  if (item.children) {
    return (
      <div 
        ref={dropdownRef}
        className="relative" 
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button 
          className={`px-2 xl:px-3 py-2 rounded-full text-sm font-medium inline-flex items-center transition-colors duration-200 ${
            isActive 
              ? 'text-white bg-primary shadow-sm' 
              : 'text-gray-700 hover:bg-slate-100 hover:text-primary'
          }`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span>{item.label}</span>
          <svg 
            className="ml-1 h-5 w-5 text-gray-500 group-hover:text-primary" 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor" 
            aria-hidden="true"
          >
            <path 
              fillRule="evenodd" 
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        </button>
        {isDropdownOpen && (
          <div 
            className="absolute z-10 -ml-4 mt-1 w-72 max-h-[70vh] overflow-y-auto rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div className="py-1">
              {item.children.map((child, index) => (
                child.to && (
                  <Link 
                    key={index}
                    to={child.to} 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary"
                    onClick={(e) => handleLinkClick(e, child.to!)}
                  >
                    {child.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // For regular menu items (links)
  if (item.href && !item.to) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="px-2 xl:px-3 py-2 rounded-full text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-slate-100 hover:text-primary"
      >
        {item.label}
      </a>
    );
  }
  
  if (!item.to) return null;
  
  return (
    <Link 
      to={item.to} 
      className={`px-2 xl:px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
        isActive 
          ? 'text-white bg-primary shadow-sm' 
          : 'text-gray-700 hover:bg-slate-100 hover:text-primary'
      }`}
      onClick={(e) => handleLinkClick(e, item.to!)}
    >
      {item.label}
    </Link>
  );
};

interface MobileMenuItemProps {
  item: MenuItemType;
  location: Location;
  handleLinkClick: (e: React.MouseEvent<HTMLAnchorElement>, path: string) => void;
  closeMobileMenu: () => void;
}

const MobileMenuItem: React.FC<MobileMenuItemProps> = ({ item, location, handleLinkClick, closeMobileMenu }) => {
  const isActive = item.to && location.pathname === item.to;
  
  // Handle click on mobile menu item
  const handleMobileItemClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string): void => {
    handleLinkClick(e, path);
    closeMobileMenu();
  };
  
  // For items with children (dropdowns)
  if (item.children) {
    return (
      <>
        <div className="px-3 py-2 font-medium text-gray-700">{item.label}</div>
        <div className="pl-5 space-y-1">
          {item.children.map((child, index) => (
            child.to && (
              <Link
                key={index}
                to={child.to}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-primary"
                onClick={(e) => handleMobileItemClick(e, child.to!)}
              >
                {child.label}
              </Link>
            )
          ))}
        </div>
      </>
    );
  }
  
  // For regular menu items (links)
  if (item.href && !item.to) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-primary"
        onClick={closeMobileMenu}
      >
        {item.label}
      </a>
    );
  }
  
  if (!item.to) return null;
  
  return (
    <Link
      to={item.to}
      className={`block px-3 py-2 rounded-md text-base font-medium ${
        isActive
          ? 'text-white bg-primary'
          : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
      }`}
      onClick={(e) => handleMobileItemClick(e, item.to!)}
    >
      {item.label}
    </Link>
  );
};

const Menu: React.FC<MenuProps> = ({ items }) => {
  const { locale } = useI18n();
  const t = locale === 'km' ? km : en;
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string): void => {
    if (location.pathname === path) {
      e.preventDefault();
    }
  };
  
  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
  };
  
  return (
    <>
      {/* Desktop Menu */}
      <nav className="hidden xl:ml-10 xl:flex xl:items-center xl:space-x-4">
        {items.map((item, index) => (
          <MenuItem 
            key={index} 
            item={item} 
            location={location} 
            handleLinkClick={handleLinkClick} 
          />
        ))}
      </nav>
      
      {/* Mobile Menu Button */}
      <div className="ml-auto -mr-2 flex items-center gap-1 xl:hidden">
        {/* Mobile Social/Docs Icons */}
        <a
          href="https://facebook.com/www.akas.co"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-500 hover:text-primary p-2"
          aria-label={t.facebook}
        >
          <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
              clipRule="evenodd"
            />
          </svg>
        </a>

        <a
          href="https://www.linkedin.com/company/akas-co/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-500 hover:text-primary p-2"
          aria-label={t.linkedin}
        >
          <FaLinkedin size={24} />
        </a>

        <a
          href="https://docs.akas.co/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-500 hover:text-primary p-2"
          aria-label={t.documentations}
        >
          <BiSolidFileDoc size={28} />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          type="button"
          className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
          aria-expanded="false"
        >
          <span className="sr-only">{t.openMainMenu}</span>
          {!isMobileMenuOpen ? (
            <svg
              className="block h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          ) : (
            <svg
              className="block h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          )}
        </button>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="xl:hidden absolute top-full left-0 right-0 bg-white shadow-lg z-50">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {items.map((item, index) => (
              <MobileMenuItem 
                key={index} 
                item={item} 
                location={location} 
                handleLinkClick={handleLinkClick} 
                closeMobileMenu={closeMobileMenu}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Menu; 